import { redactText } from '../../../shared/redact';
import { ApiError, request } from '../api';
import { getBases } from '../base';
import { appVersion, config } from '../config';
import { unrecordedFetch } from '../recorder';
import type { Target } from './targets';

export interface Proof {
  file: string;
  line: number;
  quote: string;
}

/** Two kinds, decided by the host: `found` — proofs matched the code; otherwise a guess marked as a guess. */
export interface CodeAnswer {
  found: boolean;
  answer: string;
  guess: string | null;
  proofs: Proof[];
  /** Service answers only: the tracker row it stored, and whether the code of the tab's version was there. */
  id?: number;
  snapshot?: 'exact' | 'missing';
}

export type AnswerDepth = 'simple' | 'detailed';
export type AnswerAudience = 'product' | 'developer';

/** Fields beyond the base contract — each only when the host turned it on (`ask.modes`, `ask.history`, `ask.hints`). */
export interface AskOptions {
  depth?: AnswerDepth;
  audience?: AnswerAudience;
  history?: { question: string; answer: string }[];
  hints?: boolean;
  /** No place picked: marks of the blocks on screen (Тв7а) — the service answers from the screen. */
  screen?: string[];
}

/** The screen path inside the host app: the base path (`/test`) is cut off. */
export function hostRoute(): string {
  const { host } = getBases();
  const path = window.location.pathname;
  // Хс4: a one-page app (the tracker) tells its section after `#`.
  return (host && path.startsWith(host) ? path.slice(host.length) || '/' : path) + window.location.hash;
}

/**
 * `POST <host base><path>` with the host session cookie; past the recorder (not part of a report snapshot). Errors —
 * `ApiError` with the host's words; 401 goes to `onApiError` like the widget's own requests.
 */
function questionBody(question: string, targets: Target[], options: AskOptions, clean: (text: string) => string = (text) => text) {
  return {
    question: clean(question),
    targets: targets.map((target) => ({
      kind: target.kind,
      anchors: target.anchors,
      text: target.kind === 'screen' ? null : clean(target.text),
      ...(options.hints && target.hint ? { hint: clean(target.hint) } : {}),
    })),
    route: hostRoute(),
    ...(targets.length === 0 && options.screen?.length ? { screen: options.screen } : {}),
    ...(options.depth ? { depth: options.depth } : {}),
    ...(options.audience ? { audience: options.audience } : {}),
    ...(options.history ? { history: options.history.map((turn) => ({ question: clean(turn.question), answer: turn.answer })) } : {}),
  };
}

/**
 * The service answers (`ask.service`): through the host proxy like the reports, with the tab's build (`appVersion`); the
 * texts leave cleaned, as the tracker keeps them. The answer is already stored — its row comes back as `id`.
 */
export async function askService(question: string, targets: Target[], options: AskOptions = {}): Promise<CodeAnswer> {
  const data = await request<CodeAnswer & { version?: string | null }>('/questions/ask', {
    method: 'POST',
    body: JSON.stringify({ ...questionBody(question, targets, options, redactText), version: appVersion(), pageUrl: window.location.href }),
  });
  return {
    found: data.found === true,
    answer: typeof data.answer === 'string' ? data.answer : '',
    guess: typeof data.guess === 'string' ? data.guess : null,
    proofs: Array.isArray(data.proofs) ? data.proofs : [],
    ...(typeof data.id === 'number' ? { id: data.id } : {}),
    snapshot: data.snapshot === 'exact' ? 'exact' : 'missing',
  };
}

export async function askCode(path: string, question: string, targets: Target[], options: AskOptions = {}): Promise<CodeAnswer> {
  const body = questionBody(question, targets, options);
  let res: Response;
  try {
    res = await unrecordedFetch(`${getBases().host}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'Сервер не ответил — повторите.', 'offline');
  }
  const data = (await res.json().catch(() => null)) as (CodeAnswer & { error?: string; code?: string }) | null;
  if (!res.ok || !data) {
    const err = new ApiError(res.status, typeof data?.error === 'string' && data.error ? data.error : 'Не получилось — повторите.', data?.code);
    if (res.status === 401) {
      try {
        config().onApiError?.({ status: err.status, message: err.message, code: err.code });
      } catch {
        // The host callback must not break the widget.
      }
    }
    throw err;
  }
  return {
    found: data.found === true,
    answer: typeof data.answer === 'string' ? data.answer : '',
    guess: typeof data.guess === 'string' ? data.guess : null,
    proofs: Array.isArray(data.proofs) ? data.proofs : [],
  };
}
